import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  convertLabelMapToConfig,
  convertLabelUomMapToConfig,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { get, joinPaths } from 'Lib/Object/Path/shared';
import { getSieveSizeFromId } from '../../Helpers';

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.Preparation.LightweightAggregate`]: 'Lightweight Aggregate',
      [`${prefix}.Preparation.Method`]: 'Test Procedure',
      [`${prefix}.Preparation.MaterialWashed`]: 'Material Washed',
      [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: 'Sieves Are Added',

      [`${prefix}.WholePortion.Preparation.ConditionOfSampleTested`]: 'Condition of Sample Tested',

      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndDryMass`]: 'Tare and Material Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.DryMass`]: 'Material Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.Volume`]: 'Volume',
      [`${prefix}.WholePortion.Masses.MassMeasurement.BulkDensity`]: 'Bulk Density',

      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.TareAndWetMass`]:
        'Tare and Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMassMeasurements.WetMass`]: 'Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndWetMass`]: 'Tare and Material Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMass`]: 'Material Wet Mass',
      [`${prefix}.WholePortion.Masses.DryMass`]: 'Total Material Dry Mass',

      [`${prefix}.WholePortion.Washing.WashedDryMass`]: 'Sieve Portion Washed Material Dry Mass',

      [`${prefix}.WholePortion.Sieving.Equipment.Balance`]: 'Balance',
      [`${prefix}.WholePortion.Sieving.Equipment.SieveSet`]: 'Sieve Set',

      [`${prefix}.Results.ChartOptions.CurveFittingAlgorithm`]: 'Curve Fitting',
      [`${prefix}.Results.ChartOptions.JoinSpecificationPoints`]: 'Join specification points on chart',
      [`${prefix}.Results.D85`]: 'D85',
      [`${prefix}.Results.D60`]: 'D60',
      [`${prefix}.Results.D50`]: 'D50',
      [`${prefix}.Results.D30`]: 'D30',
      [`${prefix}.Results.D15`]: 'D15',
      [`${prefix}.Results.D10`]: 'D10',
      [`${prefix}.Results.CoefficientOfUniformity`]: 'Coefficient of Uniformity (Cu)',
      [`${prefix}.Results.CoefficientOfCurvature`]: 'Coefficient of Curvature (Cc)',
      [`${prefix}.Results.ReportPSDCurveAnalysis`]: 'D60, D30, D10, Cu and Cc',
      [`${prefix}.Results.ReportSizeOfTestPortion`]: 'Size of test portion',
      [`${prefix}.Results.EquivalentMassPassing_0_063_Sieve`]: 'Equivalent Mass of Fines',
      [`${prefix}.Results.PercentagePassing_0_063_Sieve`]: (args: IConfigFunctionArgs) => {
        const washingSieveSizeId: SieveSizeId = '0_063';
        const { AllSieveSizes: allSieveSizes } = get(args.worksheet, joinPaths(prefix, 'Equipment')) ?? {};
        return `Finer than ${getSieveSizeFromId(washingSieveSizeId, allSieveSizes)?.Caption ?? '63 µm'}`;
      },
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.Results.PercentagePassing_0_063_Sieve`]: '%',
    }),

    getSeparationLabelConfig(`${prefix}.WholePortion.Separation`),
    getSeparationLabelConfig(`${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Separation`)
  );

/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation)
 */
const getSeparationLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    }),
    getPortionLabelConfig(`${prefix}.CoarsePortion`, 'Coarse Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion`, 'Fine Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion.Subspecimen`, 'Subspecimen')
  );

/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation.CoarsePortion)
 * @param captionPrefix prefix for mass captions (e.g. 'Coarse Portion')
 */
const getPortionLabelConfig = (prefix: string, captionPrefix: string) =>
  convertLabelMapToConfig({
    [`${prefix}.Separation.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    [`${prefix}.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.DryMassBalance`]: 'Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.Oven`]: 'Oven',
    [`${prefix}.Masses.MassMeasurement.TareAndWetMass`]: `Tare and ${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.MassMeasurement.TareAndDryMass`]: `Tare and ${captionPrefix} Dry Mass`,
    [`${prefix}.Masses.MassMeasurement.TareMass`]: `Tare Mass`,
    [`${prefix}.Masses.DryMassCombinedWithSeparationLosses`]: 'Combined Fine Portion Mass',
    [`${prefix}.Masses.ExpectedDryMassCombinedWithSeparationLosses`]: 'Expected Combined Fine Portion Mass ',
    [`${prefix}.Masses.WetMass`]: `${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.DryMass`]: `${captionPrefix} Dry Mass`,
    [`${prefix}.Masses.MassUpscaleFactor`]: 'Correction Factor',
    [`${prefix}.Sieving.Equipment.Balance`]: 'Balance',
    [`${prefix}.Sieving.Equipment.SieveSet`]: 'Sieve Set',
    [`${prefix}.Sieving.ExpectedIndividualPanMassRetained`]: 'Expected Mass Passing',
    [`${prefix}.Sieving.UpscaledPanMass`]: 'Upscaled Pan Mass',
    [`${prefix}.Washing.WashedDryMass`]: `${captionPrefix} Washed Material Dry Mass`,
  });

export default getLabelConfig;
